import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Injector,
    Input,
    OnInit,
    output,
    inject,
    ViewEncapsulation,
} from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { IForum, IForumPost } from '@fi-shared/fi-forum/fi-forum';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { IItemsListAction2, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { FiForumSharedService } from '@fi-shared/fi-forum/fi-forum-shared.service';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiCommentsService } from '@fi-shared/fi-comments/fi-comments.service';
import { isDefined } from '@fi-shared/utils';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';

@Component({
    selector: 'fi-forum-view',
    templateUrl: './fi-forum-view.component.html',
    styleUrls: ['../fi-forum-list/fi-forum-list.component.scss', './fi-forum-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-forum-view' },
    standalone: false,
})
export class FiForumViewComponent extends FiRemoteStateFullDataSource<IForumPost> implements OnInit {
    @Input() forum: IForum;
    @Input() sortList$: Observable<void> = new Observable<void>();
    public onForumEdit = output<void>();
    public onForumDelete = output<void>();
    public onForumNewPost = output<void>();

    public readonly defaultSortColumnName = 'LatestActivity';
    public readonly defaultSortDirection = true;
    public readonly objectType = ObjectTypes.ForumPost;
    public readonly itemsListType = FiItemsListType.ForumPostListData;

    private accessToApplicationTypeService = inject(AccessToApplicationTypeService);
    private forumService = inject(FiForumSharedService);
    private fiSignalRService = inject(FiSignalRService);
    private fiSignalRHelperService = inject(FiSignalRHelperService);
    private notificationHelper = inject(FiNotificationHelperService);
    private detectDevices = inject(DetectDevicesService);
    private commentsService = inject(FiCommentsService);

    protected readonly sideListOptions: ISideListOptionsItem[] = [
        {
            value: SideListOptionType.SharedWithMe,
            text: this.appTextService.get('forum.all_posts'),
        },
        {
            value: SideListOptionType.CreatedBy,
            text: this.appTextService.get('forum.my_posts'),
        },
        {
            value: SideListOptionType.ForumNewPosts,
            text: this.appTextService.get('forum.updated_posts'),
        },
    ];
    protected readonly actions: IItemsListAction2[] = this.getForumViewActions();
    private userMinDateForNotifications: Date = new Date(this.userDataService.userData?.MinDateForNotifications);

    protected readonly SideListOptionTypes = SideListOptionType;
    protected readonly FiStateNames = FiStateNames;
    protected readonly FiProfileSizes = FiProfileSizes;

    @HostBinding('class.mobile-view') get isMobileView(): boolean {
        return this.detectDevices.isMobilePhone();
    }

    constructor(injector: Injector) {
        super(injector);
    }

    ngOnInit(): void {
        this.setLoading();
        this.sortList$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.doSort(this.defaultSortColumnName, this.defaultSortDirection);
        });

        // https://chainformation.atlassian.net/browse/FI-4572
        // mark forum posts as read
        this.forumService.markForumPostsAsReadByForumIds([this.forum.Id]).then((forumPostIdsMarkedAsRead) => {
            this.updateItems((items) => {
                return items.map((forumPost) => {
                    this.notificationHelper.setIsSeen(forumPost);
                    return forumPost;
                });
            });
            this.fiSignalRHelperService.onMarkForumPostsAsRead(forumPostIdsMarkedAsRead);
        });

        this.fiSignalRService.commentsUpdatedEvent.subscribe((data) => {
            const items = this.items();
            const currentForumPost = items.find(
                (forumPost) => data.ObjectId === forumPost.Id && data.ObjectType === ObjectTypes.ForumPost,
            );
            if (isDefined(currentForumPost)) {
                this.commentsService
                    .getCommentsCount({
                        objectIds: [currentForumPost.Id],
                        objectTypes: [ObjectTypes.ForumPost],
                    })
                    .then((commentsCountData) => {
                        this.updateItems((items) => {
                            return items.map((forumPost) => {
                                const commentsCount = commentsCountData
                                    .filter(
                                        (item) =>
                                            item.ObjectId === forumPost.Id && item.ObjectType === ObjectTypes.ForumPost,
                                    )
                                    .reduce((sum, item) => sum + item.Count, 0);
                                return { ...forumPost, CommentsCount: commentsCount };
                            });
                        });
                        this.cdr.detectChanges();
                    });
            }
        }, this.ngOnDestroy$);
    }

    private setLoading() {
        /*this.isLocalLoadingHandler = true;
        this._loading$.next(true);*/
    }

    private getForumViewActions(): IItemsListAction2[] {
        return [
            {
                title: this.appTextService.get('forum.delete_post'),
                onClick: (item: IForumPost) => this.onDeleteSingleItem(item),
                disabled: (item: IForumPost) => !item.HasDeleteAccess,
            },
            {
                title: this.appTextService.get('common.edit'),
                onClick: (item: IForumPost) => this.editForumPost(item),
                disabled: (item: IForumPost) => !item.HasEditAccess,
            },
        ];
        // todo: add later https://chainformation.atlassian.net/browse/FI-4498
        // this.actions.push({
        //     title: this.appTextService.get('post.mark_as_read'),
        //     onClick: () => {
        //         this.fiLoading.trackPromise(this.forumService.markForumPostsAsVisited(this.forum.Id, this.selectedItems.map(forum => forum.Id)))
        //             .then(forumPostIdsMarkedAsSeen => {
        //                 this.fiSignalRHelperService.onMarkForumPostsAsVisited(forumPostIdsMarkedAsSeen)
        //                 this.selectedItems.forEach(item => {
        //                     this.notificationHelper.setIsVisited(item);
        //                 });
        //                 this.clearSelection();
        //                 this.cdr.markForCheck();
        //             });
        //     },
        //     disabled: () => !this.selectedItems.find(item => this.notificationHelper.isNotVisited(item))
        // });
    }

    private editForumPost(item: IForumPost): void {
        this.fiStateService.go(FiStateNames.Forum.Post.View, {
            id: item.Id,
            forumId: this.forum.Id,
            isEditMode: true,
        });
    }

    protected hasBrandedColor(item: IForumPost): boolean {
        return (
            this.userMinDateForNotifications <= new Date(item.ChangedOn) && this.notificationHelper.isNotVisited(item)
        );
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: false,
        };
        return this.forumService.getForumPostsListData(this.forum.Id, parameter);
    }

    protected deleteFn(itemsToDelete: IForumPost[]): IFiHttpPromise<void> {
        return this.forumService.deleteForumPosts(itemsToDelete.map((forum) => forum.Id));
    }

    protected createNewForumPost($event: MouseEvent): void {
        $event.preventDefault();
        this.onForumNewPost.emit();
    }
}
